import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDocusignEnvelopeStatusBySqsMessageIdRequest,
  fetchDocusignSubmissionDataBySqsMessageIdRequest,
  resetPolicyApplicationStateStatus,
} from '@client/model/policyApplicationModel';
import {
  selectPolicyApplicationModel,
  selectPolicyApplicationsStatus,
} from '@client/model/selectors';
import { useToast } from '@client/components/hooks/use-toast';

interface UseDocuSignEnvelopeStatusOptions {
  clientPolicyApplicationId: string | null | undefined;
  onEnvelopeSentSuccess?: () => void;
}

interface UseDocuSignEnvelopeStatusReturn {
  storedMessageId: string | null;
  docusignEnvelopeStatus: string | null;
  embeddedSigningData: any;
  policyApplicationModelStatus: string | null;
  policyApplicationStatus: string | null;
  isPostingEnvelope: boolean;
  isFetchingEnvelopeStatus: boolean;
  hasSubmittedEnvelope: boolean;
  fetchEnvelopeStatus: () => void;
  docusignSubmissionDataComparison: null | DocusignSubmissionDataComparisonResponse;
  fetchSubmissionData: () => void;
}

export function useDocuSignEnvelopeStatus({
  clientPolicyApplicationId,
  onEnvelopeSentSuccess,
}: UseDocuSignEnvelopeStatusOptions): UseDocuSignEnvelopeStatusReturn {
  const dispatch = useDispatch();

  const { toast } = useToast();
  const [storedMessageId, setStoredMessageId] = useState('');

  // Generate localStorage key specific to the clientPolicyApplicationId
  const getLocalStorageKey = useCallback(() => {
    if (!clientPolicyApplicationId) return null;
    return `docusignApplicationId_${clientPolicyApplicationId}`;
  }, [clientPolicyApplicationId]);

  const {
    docusignSubmissionDataComparison,
    embeddedSigningData,
    status: policyApplicationModelStatus,
    docusignEnvelopeStatus,
  } = useSelector(selectPolicyApplicationModel);

  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const isPostingEnvelope = policyApplicationStatus === 'postingEnvelope';
  const isFetchingEnvelopeStatus = policyApplicationModelStatus === 'fetchingEnvelopeStatus';
  const localStorageKey = getLocalStorageKey();
  const localMessageId = localStorageKey ? localStorage.getItem(localStorageKey) : '';
  const hasSubmittedEnvelope = localMessageId ? true : false;

  const fetchEnvelopeStatus = useCallback(() => {
    const localStorageKey = getLocalStorageKey();
    if (!localStorageKey) return;

    const stored = localStorage.getItem(localStorageKey);
    if (!stored) return;

    try {
      const parsedData = JSON.parse(stored);
      const messageId = parsedData?.messageId;
      const localCPAId = parsedData?.clientPolicyApplicationId;
      setStoredMessageId(messageId);

      if (
        messageId &&
        localCPAId &&
        policyApplicationModelStatus !== 'fetchingEnvelopeStatus' &&
        policyApplicationModelStatus !== 'fetchingEnvelopeStatusComplete'
      ) {
        dispatch(
          fetchDocusignEnvelopeStatusBySqsMessageIdRequest({
            sqsMessageId: messageId,
            queryParams: { clientPolicyApplicationId: localCPAId },
          }),
        );
      }
    } catch (error) {
      console.error('Failed to parse docusignApplicationId from localStorage:', error);
      localStorage.removeItem(localStorageKey);
    }
  }, [dispatch, policyApplicationModelStatus, getLocalStorageKey]);

  // Store messageId in localStorage when envelope is posted successfully
  useEffect(() => {
    if (
      embeddedSigningData?.messageId &&
      policyApplicationModelStatus === 'postingEnvelopeComplete' &&
      clientPolicyApplicationId
    ) {
      const localStorageKey = getLocalStorageKey();
      if (localStorageKey) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            messageId: embeddedSigningData.messageId,
            clientPolicyApplicationId,
          }),
        );
      }
    }
  }, [
    policyApplicationModelStatus,
    embeddedSigningData?.messageId,
    clientPolicyApplicationId,
    getLocalStorageKey,
  ]);

  // Fetch envelope status on mount
  useEffect(() => {
    fetchEnvelopeStatus();
  }, [fetchEnvelopeStatus]);

  const fetchSubmissionData = useCallback(() => {
    if (!storedMessageId || !clientPolicyApplicationId) return;

    if (
      policyApplicationModelStatus !== 'fetchingEnvelopeSubmissionDataComparisonStatus' &&
      policyApplicationModelStatus !== 'fetchingEnvelopeSubmissionDataComparisonStatusComplete'
    ) {
      dispatch(
        fetchDocusignSubmissionDataBySqsMessageIdRequest({
          sqsMessageId: storedMessageId,
          queryParams: { clientPolicyApplicationId },
        }),
      );
    }
  }, [dispatch, storedMessageId, clientPolicyApplicationId, policyApplicationModelStatus]);

  useEffect(() => {
    fetchSubmissionData();
  }, [storedMessageId]);

  // Handle success/error states with toast notifications
  useEffect(() => {
    if (policyApplicationStatus === 'postingEnvelopeComplete') {
      toast({
        title: 'Envelope Sent Successfully',
        description: 'The application has been sent for signature.',
        variant: 'default',
        className: 'bg-green-50 text-green-800 -translate-y-2',
      });
      onEnvelopeSentSuccess?.();
      dispatch(resetPolicyApplicationStateStatus());
    } else if (policyApplicationStatus === 'postingEnvelopeError') {
      toast({
        title: 'Something Went Wrong',
        description:
          'We were unable to send the application for signature. Please try again or contact support if the issue continues.',
        className: '-translate-y-2 bg-red-50 text-red-800 border border-red-200',
      });
      dispatch(resetPolicyApplicationStateStatus());
    }
  }, [policyApplicationStatus, toast, dispatch, onEnvelopeSentSuccess]);

  return {
    storedMessageId,
    docusignEnvelopeStatus,
    embeddedSigningData,
    policyApplicationModelStatus,
    policyApplicationStatus,
    isPostingEnvelope,
    isFetchingEnvelopeStatus,
    hasSubmittedEnvelope,
    fetchEnvelopeStatus,
    fetchSubmissionData,
    docusignSubmissionDataComparison,
  };
}
